// Concierge Partner form component
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { conciergePartnerSchema, type ConciergePartnerInput } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'

interface Props {
  defaultValues?: Partial<ConciergePartnerInput>
  onSubmit: (data: ConciergePartnerInput) => void
  onCancel?: () => void
  loading?: boolean
}

export function ConciergePartnerForm({ defaultValues, onSubmit, onCancel, loading }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConciergePartnerInput>({
    resolver: zodResolver(conciergePartnerSchema),
    defaultValues,
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          {t('fields.name')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('name')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
          {t('fields.category')} <span className="text-red-500">*</span>
        </label>
        <select
          {...register('category')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          <option value="">{t('common.select')}</option>
          <option value="hospitality">{t('concierge.categories.hospitality')}</option>
          <option value="music">{t('concierge.categories.music')}</option>
          <option value="wine">{t('concierge.categories.wine')}</option>
          <option value="sport">{t('concierge.categories.sport')}</option>
          <option value="maritime">{t('concierge.categories.maritime')}</option>
          <option value="auto">{t('concierge.categories.auto')}</option>
        </select>
        {errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="logoUrl" className="block text-sm font-medium text-gray-700">
          {t('concierge.fields.logoUrl')}
        </label>
        <input
          {...register('logoUrl')}
          type="text"
          placeholder="/images/partners/..."
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.logoUrl && (
          <p className="mt-1 text-sm text-red-600">{errors.logoUrl.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
